import { withAITracking } from '@microsoft/applicationinsights-react-js';
import Box from '@mui/material/Box';
import { Theme, useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import { GridColDef, GridRowIdGetter } from '@mui/x-data-grid-pro';
import { useSuspenseQuery } from '@tanstack/react-query';
import { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { useProductCenterApi } from 'api';
import { unitKitsQuery } from 'api/queries/SoftwareKitQueries';
import { KitDto } from 'api/responses/models/SoftwareKit/KitDto';
import { ParseKeys } from 'i18next';
import {
	DisplayAlignment,
	GridOrCardToggleButton,
} from 'library/components/controls/buttons/GridOrCardToggleButton';
import { StyledBoxCardContainer } from 'library/components/controls/cards/CardContainer';
import { DataGridToolbar } from 'library/components/controls/datagrid/DataGridToolbar';
import { LocalizedDataGrid } from 'library/components/controls/datagrid/LocalizedDataGrid';
import { InfinityScroll } from 'library/components/infinityScroll/InfinityScroll';
import { getLocalizedDateFormat } from 'library/formatters/DateFormatter';
import Variables from 'library/utils/themes/Variables';
import { reactPlugin } from 'main/azureInsights/config';
import { useResolvedUnitIds } from 'modules/Shared/Components/Hooks/useNumberParam';
import {
	KitCard,
	renderDocuments,
	renderPrice,
} from 'modules/UnitOverview/Components/KitCard';

const KitsSectionPage = (): React.JSX.Element => {
	const { t } = useTranslation(['unitOverview', 'unitCreation', 'common']);
	const theme = useTheme<Variables>();
	const unitIds = useResolvedUnitIds();
	const cardOnlyMode = useMediaQuery((theme: Theme) =>
		theme.breakpoints.down('sm')
	);
	const [alignment, setAlignment] = useState<DisplayAlignment>('grid');

	const renderAlignment = cardOnlyMode ? 'card' : alignment;

	const { api } = useProductCenterApi();
	const { data } = useSuspenseQuery(unitKitsQuery(api, unitIds.id));

	const columns = useMemo(
		(): GridColDef<KitDto>[] => [
			{
				field: 'installationType',
				headerName: t('unitOverview:installation-type-header'),
				flex: 4,
				minWidth: 80,
				valueGetter: (_value, { installationType }) =>
					t(
						`unitCreation:InstallationPurposeValue_${installationType}` as ParseKeys<
							['unitOverview', 'unitCreation']
						>
					),
			},
			{
				field: 'driveLine',
				headerName: t('unitOverview:driveline-header'),
				flex: 4,
				minWidth: 80,
			},
			{
				field: 'kitNumber',
				headerName: t('unitOverview:kit-number-header'),
				flex: 4,
				minWidth: 80,
			},
			{
				field: 'description',
				headerName: t('unitOverview:description-header'),
				flex: 4,
				minWidth: 120,
			},
			{
				field: 'function',
				headerName: t('unitOverview:function-header'),
				flex: 4,
				minWidth: 120,
			},
			{
				field: 'documents',
				headerName: t('unitOverview:documents-header'),
				flex: 4,
				sortable: false,
				filterable: false,
				minWidth: 120,
				renderCell: (params) => renderDocuments(params.row.documents),
				display: 'flex',
			},
			{
				field: 'price',
				headerName: t('unitOverview:suggested-retail-header'),
				flex: 4,
				minWidth: 80,
				valueGetter: (_value, { currency, price }) =>
					renderPrice(t, price, currency),
			},
			{
				field: 'dateOf',
				headerName: t('common:date-header'),
				flex: 4,
				type: 'date',
				minWidth: 120,
				valueFormatter: (value) => getLocalizedDateFormat(value),
			},
			{
				field: 'status',
				headerName: t('unitOverview:status-header'),
				flex: 4,
				minWidth: 100,
			},
			{
				field: 'orderNumber',
				headerName: t('unitOverview:order-header'),
				flex: 4,
				minWidth: 120,
			},
			{
				field: 'orderBy',
				headerName: t('unitOverview:order-by-header'),
				flex: 4,
				minWidth: 120,
			},
			{
				field: 'invoiceTo',
				headerName: t('unitOverview:invoice-to-header'),
				flex: 4,
				minWidth: 120,
			},
			{
				field: 'performedBy',
				headerName: t('unitOverview:performed-by-header'),
				flex: 4,
				minWidth: 120,
			},
		],
		[t]
	);

	const getGridRowId: GridRowIdGetter<KitDto> = useCallback(
		({ driveLine, kitNumber }) => driveLine + kitNumber,
		[]
	);

	return (
		<>
			<Box
				sx={{
					mb: 1,
				}}>
				<GridOrCardToggleButton
					sx={(theme) => ({
						display: cardOnlyMode ? 'none' : 'block',
						minWidth: theme.spacing(10),
					})}
					value={alignment}
					onChange={setAlignment}
				/>
			</Box>
			{renderAlignment === 'card' ? (
				<StyledBoxCardContainer>
					<InfinityScroll data={data} Item={KitCard} />
				</StyledBoxCardContainer>
			) : (
				<Box
					sx={{
						height: theme.custom.dataGridHeight,
					}}>
					<LocalizedDataGrid
						showToolbar
						initialState={{
							density: 'compact',
						}}
						columns={columns}
						getRowId={getGridRowId}
						rows={data}
						slots={{ toolbar: DataGridToolbar }}
					/>
				</Box>
			)}
		</>
	);
};

export default withAITracking(
	reactPlugin,
	KitsSectionPage,
	'UnitOverviewKitsSectionPage',
	'application-insight-container'
);
